import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    View, StyleSheet, Text, TouchableOpacity, ScrollView,
    Image, TextInput, Animated, Dimensions, Platform,
    RefreshControl, ActivityIndicator, FlatList
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { getData } from '../../services/api';
import { H1, H2, H3, Body1, Body2, Caption, ButtonText } from '../../components/common/TextStyles';
import { getShadow } from '../../theme/useTheme';
import MentorProfileModal from '../../components/mentor/MentorProfileModal';
import { getSessionsByStatus, getLiveKitToken, LIVEKIT_WS_URL, createLiveKitRoom } from '../../services/livekit';
import { useAuth } from '../../contexts/AuthContext';
import { Alert } from 'react-native';

const { width: SCREEN_WIDTH } = Dimensions.get('window');

// Theme colors for Mentor section
const MENTOR_PRIMARY = '#7c3aed';      // Deep violet
const MENTOR_SECONDARY = '#a78bfa';    // Soft purple
const MENTOR_ACCENT = '#c4b5fd';       // Light lavender
const MENTOR_WARM = '#f59e0b';         // Warm amber for highlights
const MENTOR_BG = '#FAF5FF';           // Very faint purple tint

// Mentor specialty categories
const CATEGORIES = [
    { id: 'all', label: 'All', icon: 'grid-outline', color: MENTOR_PRIMARY },
    { id: 'business', label: 'Business', icon: 'briefcase-outline', color: '#10B981' },
    { id: 'tech', label: 'Tech', icon: 'code-slash-outline', color: '#3B82F6' },
    { id: 'career', label: 'Career', icon: 'trending-up-outline', color: '#F59E0B' },
    { id: 'creative', label: 'Creative', icon: 'color-palette-outline', color: '#EC4899' },
    { id: 'life', label: 'Life Coach', icon: 'heart-outline', color: '#EF4444' },
];

const MentorScreen = () => {
    const insets = useSafeAreaInsets();
    const navigation = useNavigation();
    const scrollY = useRef(new Animated.Value(0)).current;

    const [mentors, setMentors] = useState([]);
    const [filteredMentors, setFilteredMentors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [selectedMentor, setSelectedMentor] = useState(null);
    const [mentorModalVisible, setMentorModalVisible] = useState(false);
    const [liveSpaces, setLiveSpaces] = useState([]);
    const [joiningId, setJoiningId] = useState(null);
    const { user } = useAuth();

    // Animations
    const fadeAnim = useRef(new Animated.Value(0)).current;
    const slideAnim = useRef(new Animated.Value(30)).current;

    const fetchMentors = useCallback(async () => {
        try {
            const data = await getData('/user/mentors');
            if (data) {
                setMentors(data);
                setFilteredMentors(data);
            }
        } catch (error) {
            console.error('Error fetching mentors:', error);
            // Use placeholder data if API fails
            const placeholders = [
                {
                    id: 1, name: 'Billups Omido', mentorType: 'Business Strategy Coach',
                    mentorBio: 'Experienced entrepreneur with 15+ years helping youth build sustainable businesses across East Africa.',
                    profileImageUrl: 'https://i.pravatar.cc/300?u=mentor1',
                    email: 'sarah@youthconnect.com', phone: '+254712345678',
                    socialLinks: { instagram: 'https://instagram.com/sarahm', linkedin: 'https://linkedin.com/in/sarahm' },
                    points: 1250
                },
                {
                    id: 2, name: 'David Masankala', mentorType: 'Tech Mentor',
                    mentorBio: 'Full-stack developer and tech leader passionate about bridging the digital divide for African youth.',
                    profileImageUrl: 'https://i.pravatar.cc/300?u=mentor2',
                    email: 'james@youthconnect.com',
                    socialLinks: { twitter: 'https://twitter.com/jameso', linkedin: 'https://linkedin.com/in/jameso' },
                    points: 980
                },
                {
                    id: 3, name: 'Amara Kimani', mentorType: 'Career Development Coach',
                    mentorBio: 'Career counselor specializing in youth empowerment, CV building, and interview preparation.',
                    profileImageUrl: 'https://i.pravatar.cc/300?u=mentor3',
                    email: 'amara@youthconnect.com',
                    socialLinks: { instagram: 'https://instagram.com/amarak' },
                    points: 870
                },
                {
                    id: 4, name: 'David Njoroge', mentorType: 'Creative Arts Mentor',
                    mentorBio: 'Award-winning filmmaker and creative director helping young creatives find their voice and market.',
                    profileImageUrl: 'https://i.pravatar.cc/300?u=mentor4',
                    email: 'david@youthconnect.com',
                    socialLinks: { instagram: 'https://instagram.com/davidn', twitter: 'https://twitter.com/davidn' },
                    points: 720
                },
                {
                    id: 5, name: 'Faith Akinyi', mentorType: 'Life Coach',
                    mentorBio: 'Certified life coach focused on mindset transformation, goal setting, and holistic youth development.',
                    profileImageUrl: 'https://i.pravatar.cc/300?u=mentor5',
                    email: 'faith@youthconnect.com',
                    socialLinks: { linkedin: 'https://linkedin.com/in/faitha' },
                    points: 650
                },
            ];
            setMentors(placeholders);
            setFilteredMentors(placeholders);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchLiveSpaces = useCallback(async () => {
        try {
            const data = await getSessionsByStatus('live');
            if (data) {
                setLiveSpaces(data);
            }
        } catch (error) {
            console.error('Error fetching live spaces:', error);
        }
    }, []);

    useEffect(() => {
        fetchMentors();
        fetchLiveSpaces();
        // Entrance animation
        Animated.parallel([
            Animated.timing(fadeAnim, { toValue: 1, duration: 600, useNativeDriver: true }),
            Animated.timing(slideAnim, { toValue: 0, duration: 600, useNativeDriver: true }),
        ]).start();
    }, []);

    // Filter mentors by search query and category
    useEffect(() => {
        let filtered = mentors;

        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(m =>
                m.name?.toLowerCase().includes(query) ||
                m.mentorType?.toLowerCase().includes(query) ||
                m.mentorBio?.toLowerCase().includes(query)
            );
        }

        if (selectedCategory !== 'all') {
            filtered = filtered.filter(m => {
                const type = (m.mentorType || '').toLowerCase();
                switch (selectedCategory) {
                    case 'business': return type.includes('business') || type.includes('entrepreneur');
                    case 'tech': return type.includes('tech') || type.includes('software') || type.includes('developer');
                    case 'career': return type.includes('career') || type.includes('development');
                    case 'creative': return type.includes('creative') || type.includes('art') || type.includes('design');
                    case 'life': return type.includes('life') || type.includes('coach') || type.includes('wellness');
                    default: return true;
                }
            });
        }

        setFilteredMentors(filtered);
    }, [searchQuery, selectedCategory, mentors]);

    const onRefresh = useCallback(() => {
        setRefreshing(true);
        Promise.all([fetchMentors(), fetchLiveSpaces()]).finally(() => setRefreshing(false));
    }, [fetchMentors, fetchLiveSpaces]);

    const handleMentorPress = (mentor) => {
        setSelectedMentor(mentor);
        setMentorModalVisible(true);
    };

    const handleViewFullProfile = (mentor) => {
        setMentorModalVisible(false);
        setSelectedMentor(null);
        navigation.navigate('MentorDetail', { mentor });
    };

    const handleJoinSpace = async (space) => {
        try {
            setJoiningId(space.id);
            const participantName = user?.name || user?.email || 'Anonymous';
            const participantId = String(user?.id || Date.now());

            // Check if user is the mentor for this session
            const isHost = space.mentorId == user?.id || space.mentorName === (user?.name || user?.email);
            
            const isOffline = space.isLive && space.mentorOnline === false;

            // If mentor is starting a scheduled room or resuming an offline room, transition it
            if (isHost && (space.status === 'scheduled' || isOffline)) {
                try {
                    await createLiveKitRoom({
                        roomName: space.roomName,
                        mentorId: user?.id,
                        topic: space.topic
                    });
                } catch (creErr) {
                    console.warn('[Spaces] Could not activate room specifically, continuing to token request');
                }
            }

            let token, wsUrl;
            try {
                const res = await getLiveKitToken({
                    roomName: space.roomName,
                    participantName,
                    participantId,
                    role: isHost ? 'host' : 'participant'
                });
                token = res.token;
                wsUrl = res.wsUrl || LIVEKIT_WS_URL;
            } catch (apiErr) {
                const is403 = apiErr?.status === 403 || apiErr?.response?.status === 403;
                if (is403) token = 'ACCOUNT_DEACTIVATED';
                else token = 'DEMO_TOKEN_NOT_REAL';
                wsUrl = LIVEKIT_WS_URL;
            }

            navigation.navigate('MentorLiveRoom', {
                roomName: space.roomName,
                topic: space.topic,
                mentorName: space.mentorName,
                token,
                wsUrl,
                participantName,
                isHost: isHost,
                sessionId: space.id
            });
        } catch (err) {
            console.error('[Spaces] Join error:', err);
            Alert.alert('Error', 'Could not join the space. Please try again.');
        } finally {
            setJoiningId(null);
        }
    };

    const headerOpacity = scrollY.interpolate({
        inputRange: [0, 80],
        outputRange: [1, 0],
        extrapolate: 'clamp',
    });

    return (
        <View style={styles.container}>
            {/* Background */}
            <View style={styles.bgGradientContainer}>
                <LinearGradient
                    colors={['#5b21b6', '#7c3aed', '#8b5cf6']}
                    style={StyleSheet.absoluteFill}
                />
                <View style={styles.auraContainer}>
                    <View style={[styles.aura, styles.aura1]} />
                    <View style={[styles.aura, styles.aura2]} />
                    <View style={[styles.aura, styles.aura3]} />
                </View>
            </View>

            {/* Header */}
            <View style={[styles.headerWrapper, { paddingTop: insets.top }]}>
                <View style={styles.headerContent}>
                    <TouchableOpacity
                        style={styles.backButton}
                        onPress={() => navigation.goBack()}
                    >
                        <Ionicons name="arrow-back" size={22} color="#FFFFFF" />
                    </TouchableOpacity>
                    <View style={styles.headerCenter}>
                        <View style={styles.headerIconBadge}>
                            <Ionicons name="people" size={20} color={MENTOR_ACCENT} />
                        </View>
                        <View>
                            <Text style={styles.headerTitle}>Mentors</Text>
                            <Text style={styles.headerSubtitle}>LEARN · GROW · CONNECT</Text>
                        </View>
                    </View>
                    <View style={styles.headerRight}>
                        <TouchableOpacity
                            style={[styles.headerActionBtn, { marginRight: 8 }]}
                            onPress={() => navigation.navigate('MentorSpaces')}
                        >
                            <Ionicons name="planet-outline" size={22} color="#FFFFFF" />
                        </TouchableOpacity>
                        <TouchableOpacity style={styles.headerActionBtn}>
                            <Ionicons name="filter-outline" size={22} color="rgba(255,255,255,0.9)" />
                        </TouchableOpacity>
                    </View>
                </View>
            </View>

            <Animated.ScrollView
                style={styles.scrollView}
                contentContainerStyle={[styles.scrollContent, { paddingBottom: insets.bottom + 180 }]}
                showsVerticalScrollIndicator={false}
                onScroll={Animated.event(
                    [{ nativeEvent: { contentOffset: { y: scrollY } } }],
                    { useNativeDriver: true }
                )}
                scrollEventThrottle={16}
                refreshControl={
                    <RefreshControl
                        refreshing={refreshing}
                        onRefresh={onRefresh}
                        tintColor="#FFFFFF"
                    />
                }
            >
                {/* Hero Stats Banner */}
                <Animated.View style={[styles.heroBanner, { opacity: headerOpacity }]}>
                    <View style={styles.statsRow}>
                        <View style={styles.heroPill}>
                            <Ionicons name="star" size={14} color="#FBBF24" />
                            <Text style={styles.heroPillText}>{mentors.length} Mentors</Text>
                        </View>
                        <View style={styles.heroPill}>
                            <Ionicons name="flash" size={14} color="#34D399" />
                            <Text style={styles.heroPillText}>Available Now</Text>
                        </View>
                        <View style={styles.heroPill}>
                            <Ionicons name="globe-outline" size={14} color="#60A5FA" />
                            <Text style={styles.heroPillText}>5 Categories</Text>
                        </View>
                    </View>
                </Animated.View>

                {/* Content Surface */}
                <Animated.View style={[
                    styles.contentSurface,
                    {
                        opacity: fadeAnim,
                        transform: [{ translateY: slideAnim }]
                    }
                ]}>
                    {/* Search Bar */}
                    <View style={styles.searchContainer}>
                        <View style={styles.searchBar}>
                            <Ionicons name="search" size={18} color="#9CA3AF" />
                            <TextInput
                                style={styles.searchInput}
                                placeholder="Search mentors by name or specialty..."
                                placeholderTextColor="#9CA3AF"
                                value={searchQuery}
                                onChangeText={setSearchQuery}
                            />
                            {searchQuery.length > 0 && (
                                <TouchableOpacity onPress={() => setSearchQuery('')}>
                                    <Ionicons name="close-circle" size={18} color="#9CA3AF" />
                                </TouchableOpacity>
                            )}
                        </View>
                    </View>

                    {/* Category Filter */}
                    <View style={styles.categorySection}>
                        <ScrollView
                            horizontal
                            showsHorizontalScrollIndicator={false}
                            contentContainerStyle={styles.categoryScroll}
                        >
                            {CATEGORIES.map((cat) => (
                                <TouchableOpacity
                                    key={cat.id}
                                    style={[
                                        styles.categoryChip,
                                        selectedCategory === cat.id && styles.categoryChipActive,
                                        selectedCategory === cat.id && { backgroundColor: cat.color + '18', borderColor: cat.color }
                                    ]}
                                    onPress={() => setSelectedCategory(cat.id)}
                                >
                                    <Ionicons
                                        name={cat.icon}
                                        size={16}
                                        color={selectedCategory === cat.id ? cat.color : '#6B7280'}
                                    />
                                    <Text style={[
                                        styles.categoryLabel,
                                        selectedCategory === cat.id && { color: cat.color, fontWeight: '700' }
                                    ]}>
                                        {cat.label}
                                    </Text>
                                </TouchableOpacity>
                            ))}
                        </ScrollView>
                    </View>

                    {/* Live Spaces Section (Replacing Featured Mentor) */}
                    {(() => {
                        const featuredSpace = liveSpaces.find(s => s.mentorOnline !== false) || liveSpaces[0];
                        const isFeaturedLive = featuredSpace?.mentorOnline !== false && featuredSpace?.isLive;

                        return (
                            <View style={styles.spotlightSection}>
                                <View style={styles.sectionHeaderRow}>
                                    <H2 style={styles.sectionTitle}>Live Spaces 🚀</H2>
                                    {liveSpaces.length > 0 && (
                                        <TouchableOpacity onPress={() => navigation.navigate('MentorSpaces')}>
                                            <Caption style={{ color: MENTOR_PRIMARY, fontWeight: '700' }}>See All</Caption>
                                        </TouchableOpacity>
                                    )}
                                </View>

                                {featuredSpace ? (
                                    <TouchableOpacity
                                        style={styles.spotlightCard}
                                        activeOpacity={0.9}
                                        onPress={() => handleJoinSpace(featuredSpace)}
                                    >
                                        <LinearGradient
                                            colors={isFeaturedLive ? ['#4c1d95', '#7c3aed', '#8b5cf6'] : ['#374151', '#4b5563', '#6b7280']}
                                            start={{ x: 0, y: 0 }}
                                            end={{ x: 1, y: 1 }}
                                            style={styles.spotlightGradient}
                                        >
                                            <View style={styles.spotlightDecorCircle} />
                                            <View style={styles.spotlightDecorCircle2} />

                                            <View style={styles.spotlightContent}>
                                                <View style={styles.spotlightLeft}>
                                                    <View style={styles.spotlightAvatarWrap}>
                                                        <Text style={{ fontSize: 32 }}>{featuredSpace.emoji || '🎙️'}</Text>
                                                        {isFeaturedLive && <View style={styles.spotlightOnlineDot} />}
                                                    </View>
                                                </View>
                                                <View style={styles.spotlightRight}>
                                                    {isFeaturedLive ? (
                                                        <View style={styles.liveBadge}>
                                                            <View style={styles.liveDotPulsing} />
                                                            <Text style={styles.liveBadgeText}>LIVE NOW</Text>
                                                        </View>
                                                    ) : (
                                                        <View style={[styles.liveBadge, { backgroundColor: 'rgba(255,255,255,0.2)' }]}>
                                                            <Ionicons name="moon-outline" size={10} color="#FFFFFF" style={{ marginRight: 4 }} />
                                                            <Text style={styles.liveBadgeText}>MENTOR OFFLINE</Text>
                                                        </View>
                                                    )}
                                                    <Text style={styles.spotlightName}>{featuredSpace.topic}</Text>
                                                    <Text style={styles.spotlightType}>Hosted by {featuredSpace.mentorName}</Text>
                                                    <Text style={styles.spotlightBio} numberOfLines={2}>
                                                        {isFeaturedLive
                                                            ? "Join this live conversation and learn from experts in the field."
                                                            : "This session is scheduled to be live, but the mentor is currently offline."}
                                                    </Text>
                                                    <View style={styles.spotlightActions}>
                                                        <View style={[styles.spotlightBtn, !isFeaturedLive && { backgroundColor: 'rgba(255,255,255,0.2)' }]}>
                                                            <Ionicons name={isFeaturedLive ? "headset-outline" : "notifications-outline"} size={14} color="#FFFFFF" />
                                                            <Text style={styles.spotlightBtnText}>
                                                                {joiningId === featuredSpace.id ? 'Joining...' : (isFeaturedLive ? 'Join Space' : 'Notify Me')}
                                                            </Text>
                                                        </View>
                                                        {isFeaturedLive && (
                                                            <View style={styles.spotlightBtnOutline}>
                                                                <Ionicons name="people-outline" size={14} color="#FFFFFF" />
                                                                <Text style={styles.spotlightBtnText}>
                                                                    {featuredSpace.participantCount || 0} listening
                                                                </Text>
                                                            </View>
                                                        )}
                                                    </View>
                                                </View>
                                            </View>
                                        </LinearGradient>
                                    </TouchableOpacity>
                                ) : (
                                    <TouchableOpacity
                                        style={styles.spotlightCard}
                                        activeOpacity={0.9}
                                        onPress={() => navigation.navigate('MentorSpaces')}
                                    >
                                        <LinearGradient
                                            colors={['#1f2937', '#374151', '#4b5563']}
                                            start={{ x: 0, y: 0 }}
                                            end={{ x: 1, y: 1 }}
                                            style={styles.spotlightGradient}
                                        >
                                            <View style={styles.spotlightDecorCircle} />
                                            <View style={styles.spotlightContent}>
                                                <View style={styles.spotlightLeft}>
                                                    <View style={[styles.spotlightAvatarWrap, { backgroundColor: 'rgba(255,255,255,0.1)' }]}>
                                                        <Ionicons name="planet-outline" size={40} color="#9CA3AF" />
                                                    </View>
                                                </View>
                                                <View style={styles.spotlightRight}>
                                                    <Text style={styles.spotlightName}>No live spaces right now</Text>
                                                    <Text style={styles.spotlightType}>Explore upcoming sessions</Text>
                                                    <Text style={styles.spotlightBio} numberOfLines={2}>
                                                        Check out the schedule for upcoming mentoring workshops and live Q&A sessions.
                                                    </Text>
                                                    <View style={styles.spotlightActions}>
                                                        <View style={[styles.spotlightBtn, { backgroundColor: 'rgba(255,255,255,0.2)' }]}>
                                                            <Ionicons name="calendar-outline" size={14} color="#FFFFFF" />
                                                            <Text style={styles.spotlightBtnText}>View Schedule</Text>
                                                        </View>
                                                    </View>
                                                </View>
                                            </View>
                                        </LinearGradient>
                                    </TouchableOpacity>
                                )}
                            </View>
                        );
                    })()}

                    {/* Mentor List */}
                    <View style={styles.mentorListSection}>
                        <View style={styles.sectionHeaderRow}>
                            <H2 style={styles.sectionTitle}>All Mentors</H2>
                            <Caption style={styles.mentorCount}>{filteredMentors.length} found</Caption>
                        </View>

                        {loading ? (
                            <View style={styles.loadingContainer}>
                                <ActivityIndicator size="large" color={MENTOR_PRIMARY} />
                                <Caption style={styles.loadingText}>Loading mentors...</Caption>
                            </View>
                        ) : filteredMentors.length === 0 ? (
                            <View style={styles.emptyState}>
                                <View style={styles.emptyIconWrap}>
                                    <Ionicons name="search-outline" size={48} color="#D1D5DB" />
                                </View>
                                <Body1 style={styles.emptyTitle}>No mentors found</Body1>
                                <Caption style={styles.emptySubtitle}>Try adjusting your search or filter</Caption>
                            </View>
                        ) : (
                            filteredMentors.map((mentor, index) => (
                                <MentorListCard
                                    key={mentor.id || index}
                                    mentor={mentor}
                                    index={index}
                                    onPress={() => handleMentorPress(mentor)}
                                    onRequestSession={() => navigation.navigate('MentorBooking', { mentor })}
                                />
                            ))
                        )}
                    </View>




                </Animated.View>
            </Animated.ScrollView>

            {/* 🔥 Sticky Quick Actions at Bottom 🔥 */}
            <View style={[styles.stickyFooter, { paddingBottom: insets.bottom + 12 }]}>
                <View style={styles.stickyHeader}>
                    <View style={styles.stickyHandle} />
                    <H3 style={styles.stickyTitle}>Quick Actions</H3>
                </View>
                <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={false}
                    contentContainerStyle={styles.stickyActionsScroll}
                >
                    <QuickActionCard
                        icon="planet-outline"
                        label="Live Spaces"
                        color={MENTOR_PRIMARY}
                        description="Join audio/video spaces"
                        onPress={() => navigation.navigate('MentorSpaces')}
                    />
                    <QuickActionCard
                        icon="calendar-outline"
                        label="My Sessions"
                        color="#7c3aed"
                        description="Track your sessions"
                        onPress={() => navigation.navigate('MyBookings')}
                    />
                    <QuickActionCard
                        icon="chatbubbles-outline"
                        label="Ask Question"
                        color="#10B981"
                        description="Get instant help"
                        onPress={() => { }}
                    />
                    <QuickActionCard
                        icon="star-outline"
                        label="Top Rated"
                        color="#F59E0B"
                        description="Best mentors"
                        onPress={() => { }}
                    />
                    <QuickActionCard
                        icon="compass-outline"
                        label="Explore"
                        color="#EC4899"
                        description="Browse all"
                        onPress={() => { }}
                    />
                </ScrollView>
            </View>

            {/* Mentor Profile Modal */}
            <MentorProfileModal
                visible={mentorModalVisible}
                mentor={selectedMentor}
                onClose={() => {
                    setMentorModalVisible(false);
                    setSelectedMentor(null);
                }}
            />
        </View>
    );
};

// ─── Sub-components ──────────────────────────────────────

const MentorListCard = ({ mentor, index, onPress, onRequestSession }) => {
    const cardColors = ['#7c3aed', '#10B981', '#3B82F6', '#F59E0B', '#EC4899'];
    const accentColor = cardColors[index % cardColors.length];

    return (
        <TouchableOpacity
            style={styles.mentorCard}
            activeOpacity={0.88}
            onPress={onPress}
        >
            <View style={styles.mentorCardInner}>
                <View style={styles.mentorCardLeft}>
                    <View style={[styles.mentorAvatarWrap, { borderColor: accentColor }]}>
                        <Image
                            source={{ uri: mentor.profileImageUrl || 'https://i.pravatar.cc/150' }}
                            style={styles.mentorAvatar}
                        />
                        <View style={[styles.mentorBadge, { backgroundColor: accentColor }]}>
                            <Ionicons name="star" size={8} color="#FFFFFF" />
                        </View>
                    </View>
                </View>

                <View style={styles.mentorCardCenter}>
                    <Text style={styles.mentorName} numberOfLines={1}>{mentor.name}</Text>
                    <Text style={[styles.mentorType, { color: accentColor }]} numberOfLines={1}>
                        {mentor.mentorType || 'Mentor'}
                    </Text>
                    <Text style={styles.mentorBio} numberOfLines={2}>
                        {mentor.mentorBio || 'Passionate about mentoring and youth empowerment.'}
                    </Text>

                    {/* Social icons */}
                    <View style={styles.mentorSocials}>
                        {mentor.socialLinks?.instagram && (
                            <View style={[styles.socialDot, { backgroundColor: '#EC489915' }]}>
                                <Ionicons name="logo-instagram" size={12} color="#EC4899" />
                            </View>
                        )}
                        {mentor.socialLinks?.twitter && (
                            <View style={[styles.socialDot, { backgroundColor: '#3B82F615' }]}>
                                <Ionicons name="logo-twitter" size={12} color="#3B82F6" />
                            </View>
                        )}
                        {mentor.socialLinks?.linkedin && (
                            <View style={[styles.socialDot, { backgroundColor: '#0EA5E915' }]}>
                                <Ionicons name="logo-linkedin" size={12} color="#0EA5E9" />
                            </View>
                        )}
                        {mentor.points !== undefined && (
                            <View style={styles.pointsPill}>
                                <Ionicons name="star" size={10} color="#FBBF24" />
                                <Text style={styles.pointsText}>{mentor.points}</Text>
                            </View>
                        )}
                    </View>
                </View>

                <View style={styles.mentorCardRight}>
                    <TouchableOpacity
                        style={[styles.sessionBtn, { backgroundColor: accentColor + '15' }]}
                        onPress={onRequestSession}
                    >
                        <Ionicons name="chatbubble-ellipses-outline" size={18} color={accentColor} />
                    </TouchableOpacity>
                    <Ionicons name="chevron-forward" size={18} color="#D1D5DB" />
                </View>
            </View>
        </TouchableOpacity>
    );
};

const QuickActionCard = ({ icon, label, color, onPress }) => (
    <TouchableOpacity style={styles.quickActionCard} onPress={onPress} activeOpacity={0.85}>
        <View style={[styles.quickActionIcon, { backgroundColor: color + '15' }]}>
            <Ionicons name={icon} size={17} color={color} />
        </View>
        <Text style={styles.quickActionLabel} numberOfLines={1}>{label}</Text>
    </TouchableOpacity>
);

// ─── Styles ──────────────────────────────────────

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: MENTOR_BG,
    },
    bgGradientContainer: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 280,
    },
    auraContainer: {
        ...StyleSheet.absoluteFillObject,
        overflow: 'hidden',
    },
    aura: {
        position: 'absolute',
        borderRadius: 150,
        opacity: 0.2,
    },
    aura1: {
        width: 250,
        height: 250,
        left: -80,
        top: -60,
        backgroundColor: '#c4b5fd',
    },
    aura2: {
        width: 200,
        height: 200,
        right: -40,
        top: -30,
        backgroundColor: '#a78bfa',
    },
    aura3: {
        width: 180,
        height: 180,
        right: 40,
        bottom: -50,
        backgroundColor: '#4c1d95',
    },

    // Header
    headerWrapper: {
        zIndex: 10,
        paddingBottom: 10,
    },
    headerContent: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 16,
        paddingTop: Platform.OS === 'ios' ? 8 : 14,
        paddingBottom: 8,
    },
    backButton: {
        width: 40,
        height: 40,
        borderRadius: 12,
        backgroundColor: 'rgba(255,255,255,0.15)',
        alignItems: 'center',
        justifyContent: 'center',
    },
    headerCenter: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    headerIconBadge: {
        width: 38,
        height: 38,
        borderRadius: 12,
        backgroundColor: 'rgba(196,181,253,0.22)',
        borderWidth: 1,
        borderColor: 'rgba(196,181,253,0.35)',
        alignItems: 'center',
        justifyContent: 'center',
    },
    headerTitle: {
        fontSize: 22,
        fontWeight: '800',
        color: '#FFFFFF',
        letterSpacing: 0.3,
    },
    headerSubtitle: {
        fontSize: 10,
        color: 'rgba(255,255,255,0.6)',
        fontWeight: '600',
        letterSpacing: 1.2,
    },
    headerActionBtn: {
        width: 40,
        height: 40,
        borderRadius: 12,
        backgroundColor: 'rgba(255,255,255,0.12)',
        alignItems: 'center',
        justifyContent: 'center',
    },

    // Scroll
    scrollView: {
        flex: 1,
    },
    scrollContent: {
        paddingTop: 4,
    },

    // Hero Banner
    heroBanner: {
        paddingHorizontal: 18,
        marginBottom: 16,
    },
    statsRow: {
        flexDirection: 'row',
        gap: 8,
    },
    heroPill: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5,
        backgroundColor: 'rgba(255,255,255,0.15)',
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 20,
    },
    heroPillText: {
        color: '#FFFFFF',
        fontSize: 12,
        fontWeight: '600',
    },

    // Content Surface
    contentSurface: {
        backgroundColor: MENTOR_BG,
        borderTopLeftRadius: 28,
        borderTopRightRadius: 28,
        paddingTop: 20,
        minHeight: 600,
    },

    // Search
    searchContainer: {
        paddingHorizontal: 18,
        marginBottom: 16,
    },
    searchBar: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#FFFFFF',
        borderRadius: 14,
        paddingHorizontal: 14,
        paddingVertical: Platform.OS === 'ios' ? 12 : 8,
        gap: 10,
        ...getShadow('sm'),
        borderWidth: 1,
        borderColor: '#F3F4F6',
    },
    searchInput: {
        flex: 1,
        fontSize: 14,
        color: '#111827',
        fontWeight: '500',
    },

    // Categories
    categorySection: {
        marginBottom: 20,
    },
    categoryScroll: {
        paddingHorizontal: 18,
        gap: 8,
    },
    categoryChip: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 14,
        paddingVertical: 8,
        borderRadius: 20,
        backgroundColor: '#FFFFFF',
        borderWidth: 1.5,
        borderColor: '#E5E7EB',
        marginRight: 8,
    },
    categoryChipActive: {
        borderWidth: 1.5,
    },
    categoryLabel: {
        fontSize: 13,
        fontWeight: '600',
        color: '#6B7280',
    },

    // Section Headers
    sectionHeaderRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 18,
        marginBottom: 14,
    },
    sectionTitle: {
        fontSize: 18,
        fontWeight: '800',
        color: '#111827',
    },
    mentorCount: {
        fontSize: 12,
        color: '#9CA3AF',
        fontWeight: '600',
    },

    // Featured Spotlight
    spotlightSection: {
        marginBottom: 24,
    },
    spotlightCard: {
        marginHorizontal: 18,
        borderRadius: 20,
        overflow: 'hidden',
        ...getShadow('lg'),
    },
    spotlightGradient: {
        padding: 20,
        position: 'relative',
        overflow: 'hidden',
    },
    spotlightDecorCircle: {
        position: 'absolute',
        width: 140,
        height: 140,
        borderRadius: 70,
        backgroundColor: 'rgba(255,255,255,0.08)',
        right: -30,
        top: -40,
    },
    spotlightDecorCircle2: {
        position: 'absolute',
        width: 80,
        height: 80,
        borderRadius: 40,
        backgroundColor: 'rgba(255,255,255,0.06)',
        left: -20,
        bottom: -20,
    },
    spotlightContent: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 16,
    },
    spotlightLeft: {},
    spotlightAvatarWrap: {
        position: 'relative',
    },
    spotlightAvatar: {
        width: 80,
        height: 80,
        borderRadius: 40,
        borderWidth: 3,
        borderColor: 'rgba(255,255,255,0.35)',
    },
    spotlightOnlineDot: {
        position: 'absolute',
        bottom: 2,
        right: 2,
        width: 16,
        height: 16,
        borderRadius: 8,
        backgroundColor: '#10B981',
        borderWidth: 3,
        borderColor: '#7c3aed',
    },
    spotlightRight: {
        flex: 1,
    },
    spotlightName: {
        fontSize: 18,
        fontWeight: '800',
        color: '#FFFFFF',
        marginBottom: 2,
    },
    spotlightType: {
        fontSize: 12,
        color: 'rgba(255,255,255,0.75)',
        fontWeight: '600',
        marginBottom: 6,
    },
    spotlightBio: {
        fontSize: 12,
        color: 'rgba(255,255,255,0.6)',
        lineHeight: 17,
        marginBottom: 12,
    },
    spotlightActions: {
        flexDirection: 'row',
        gap: 8,
    },
    spotlightBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5,
        backgroundColor: 'rgba(255,255,255,0.2)',
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 10,
    },
    spotlightBtnOutline: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.3)',
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 10,
    },
    spotlightBtnText: {
        color: '#FFFFFF',
        fontSize: 12,
        fontWeight: '700',
    },
    spotlightPointsBadge: {
        position: 'absolute',
        top: 12,
        right: 12,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        backgroundColor: 'rgba(0,0,0,0.25)',
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 10,
    },
    spotlightPointsText: {
        color: '#FBBF24',
        fontSize: 11,
        fontWeight: '700',
    },

    liveBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        backgroundColor: 'rgba(239,68,68,0.25)',
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 12,
        alignSelf: 'flex-start',
        marginBottom: 6,
        borderWidth: 1,
        borderColor: 'rgba(239,68,68,0.3)',
    },
    liveDotPulsing: {
        width: 7,
        height: 7,
        borderRadius: 3.5,
        backgroundColor: '#EF4444',
    },
    liveBadgeText: {
        fontSize: 10,
        fontWeight: '900',
        color: '#FFFFFF',
        letterSpacing: 0.5,
    },

    // Mentor List
    mentorListSection: {
        marginBottom: 24,
    },
    mentorCard: {
        marginHorizontal: 18,
        marginBottom: 12,
        backgroundColor: '#FFFFFF',
        borderRadius: 16,
        ...getShadow('sm'),
        borderWidth: 1,
        borderColor: '#F3F4F6',
    },
    mentorCardInner: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: 14,
        gap: 12,
    },
    mentorCardLeft: {},
    mentorAvatarWrap: {
        width: 56,
        height: 56,
        borderRadius: 20,
        borderWidth: 2,
        padding: 2,
        position: 'relative',
    },
    mentorAvatar: {
        width: '100%',
        height: '100%',
        borderRadius: 17,
    },
    mentorBadge: {
        position: 'absolute',
        bottom: -2,
        right: -2,
        width: 18,
        height: 18,
        borderRadius: 9,
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 2,
        borderColor: '#FFFFFF',
    },
    mentorCardCenter: {
        flex: 1,
    },
    mentorName: {
        fontSize: 15,
        fontWeight: '700',
        color: '#111827',
        marginBottom: 1,
    },
    mentorType: {
        fontSize: 12,
        fontWeight: '600',
        marginBottom: 4,
    },
    mentorBio: {
        fontSize: 12,
        color: '#6B7280',
        lineHeight: 16,
        marginBottom: 6,
    },
    mentorSocials: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
    socialDot: {
        width: 24,
        height: 24,
        borderRadius: 8,
        alignItems: 'center',
        justifyContent: 'center',
    },
    pointsPill: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 3,
        backgroundColor: '#FBBF2415',
        paddingHorizontal: 6,
        paddingVertical: 2,
        borderRadius: 6,
    },
    pointsText: {
        fontSize: 10,
        fontWeight: '700',
        color: '#D97706',
    },
    mentorCardRight: {
        alignItems: 'center',
        gap: 10,
    },
    sessionBtn: {
        width: 38,
        height: 38,
        borderRadius: 12,
        alignItems: 'center',
        justifyContent: 'center',
    },

    // Loading & Empty
    loadingContainer: {
        alignItems: 'center',
        paddingVertical: 40,
        gap: 12,
    },
    loadingText: {
        color: '#9CA3AF',
        fontSize: 13,
    },
    emptyState: {
        alignItems: 'center',
        paddingVertical: 40,
    },
    emptyIconWrap: {
        width: 80,
        height: 80,
        borderRadius: 40,
        backgroundColor: '#F3F4F6',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 16,
    },
    emptyTitle: {
        fontSize: 16,
        fontWeight: '700',
        color: '#374151',
        marginBottom: 4,
    },
    emptySubtitle: {
        fontSize: 13,
        color: '#9CA3AF',
    },

    // ─── Mentor Spaces Placeholder ───
    spacesSection: {
        marginBottom: 24,
    },
    comingSoonBadge: {
        backgroundColor: '#7c3aed15',
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: '#7c3aed30',
    },
    comingSoonText: {
        fontSize: 11,
        fontWeight: '700',
        color: MENTOR_PRIMARY,
    },
    spacesPlaceholder: {
        marginHorizontal: 18,
        borderRadius: 20,
        overflow: 'hidden',
        ...getShadow('md'),
    },
    spacesGradient: {
        padding: 24,
        alignItems: 'center',
    },
    spacesIconContainer: {
        position: 'relative',
        width: 100,
        height: 100,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 16,
    },
    spacesOrb1: {
        position: 'absolute',
        width: 100,
        height: 100,
        borderRadius: 50,
        backgroundColor: 'rgba(124,58,237,0.1)',
    },
    spacesOrb2: {
        position: 'absolute',
        width: 70,
        height: 70,
        borderRadius: 35,
        backgroundColor: 'rgba(124,58,237,0.15)',
    },
    spacesTitle: {
        fontSize: 18,
        fontWeight: '800',
        color: '#1F2937',
        marginBottom: 8,
        textAlign: 'center',
    },
    spacesDescription: {
        fontSize: 13,
        color: '#6B7280',
        lineHeight: 19,
        textAlign: 'center',
        paddingHorizontal: 10,
        marginBottom: 20,
    },
    spacesFeatures: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: 12,
        marginBottom: 20,
    },
    spacesFeatureItem: {
        width: '45%',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        backgroundColor: '#FFFFFF',
        paddingHorizontal: 12,
        paddingVertical: 10,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: '#F3F4F6',
    },
    spacesFeatureIcon: {
        width: 32,
        height: 32,
        borderRadius: 10,
        alignItems: 'center',
        justifyContent: 'center',
    },
    spacesFeatureText: {
        fontSize: 12,
        fontWeight: '600',
        color: '#374151',
        flex: 1,
    },
    spacesNotifyBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        backgroundColor: MENTOR_PRIMARY,
        paddingHorizontal: 20,
        paddingVertical: 12,
        borderRadius: 14,
        ...getShadow('md'),
    },
    spacesNotifyText: {
        color: '#FFFFFF',
        fontSize: 14,
        fontWeight: '700',
    },

    // Quick Actions
    quickActionsSection: {
        paddingHorizontal: 18,
        marginBottom: 16,
    },
    stickyActionsScroll: {
        paddingHorizontal: 14,
        paddingVertical: 4,
        gap: 8,
    },
    quickActionCard: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 7,
        backgroundColor: '#FFFFFF',
        borderRadius: 12,
        paddingVertical: 8,
        paddingHorizontal: 12,
        ...getShadow('sm'),
        borderWidth: 1,
        borderColor: '#F3F4F6',
    },
    quickActionIcon: {
        width: 30,
        height: 30,
        borderRadius: 9,
        alignItems: 'center',
        justifyContent: 'center',
    },
    quickActionLabel: {
        fontSize: 12,
        fontWeight: '700',
        color: '#1F2937',
    },
    quickActionDesc: {
        display: 'none',
    },

    // Sticky Footer
    stickyFooter: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: 'rgba(255,255,255,0.95)',
        borderTopWidth: 1,
        borderTopColor: '#F3E8FF',
        paddingTop: 10,
        zIndex: 100,
        ...getShadow('lg'),
    },
    stickyHeader: {
        alignItems: 'center',
        marginBottom: 10,
    },
    stickyHandle: {
        width: 36,
        height: 4,
        borderRadius: 2,
        backgroundColor: '#E5E7EB',
        marginBottom: 8,
    },
    stickyTitle: {
        fontSize: 14,
        fontWeight: '800',
        color: MENTOR_PRIMARY,
        textTransform: 'uppercase',
        letterSpacing: 1,
    },
    headerRight: {
        flexDirection: 'row',
        alignItems: 'center',
    },
});

export default MentorScreen;
